import { useEffect, useState } from "react";
import { useT } from "../lib/i18n";
import { useConfirm } from "../lib/confirm";
import { useToast } from "../lib/toast";
import { SheetShell } from "./SheetShell";
import { Button } from "./ui/Button";

export interface FieldSpec {
  key: string;
  label: string;
  type: "text" | "number" | "time";
  step?: number;
  required?: boolean;
}

interface Props<T> {
  open: boolean;
  title: string;
  fields: FieldSpec[];
  initial: Partial<T>;
  onSave: (patch: Partial<T>) => Promise<void>;
  onDelete?: () => Promise<void>;
  onClose: () => void;
}

export function EditSheet<T extends Record<string, any>>({ open, title, fields, initial, onSave, onDelete, onClose }: Props<T>) {
  const t = useT();
  const confirm = useConfirm();
  const toast = useToast();
  const [values, setValues] = useState<Record<string, any>>(initial);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setValues(initial); setErr(null); }, [initial, open]);

  if (!open) return null;

  async function save() {
    setBusy(true); setErr(null);
    try {
      const patch: Record<string, any> = {};
      for (const f of fields) {
        const v = values[f.key];
        if (v === undefined) continue;
        if (v === "" || v === null) {
          // A number OR text field the user emptied should clear (explicit null),
          // not silently preserve the old value. Time fields keep being skipped.
          if (f.type === "number" || f.type === "text") patch[f.key] = null;
          continue;
        }
        patch[f.key] = f.type === "number" ? Number(v) : v;
      }
      await onSave(patch as Partial<T>);
      toast.success(t("toast.saved"));
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function del() {
    if (!onDelete) return;
    if (!(await confirm({ message: t("confirm.delete_entry"), danger: true, confirmLabel: t("common.delete") }))) return;
    setBusy(true);
    try { await onDelete(); toast.success(t("toast.deleted")); onClose(); }
    catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }

  return (
    <SheetShell onClose={onClose}>
        <h3 className="text-lg font-semibold mt-4 mb-4">{title}</h3>

        <div className="space-y-3">
          {fields.map((f) => (
            <label key={f.key} className="block">
              <span className="text-sm text-fg-muted uppercase tracking-wider">{f.label}</span>
              <input
                type={f.type === "time" ? "time" : f.type === "number" ? "number" : "text"}
                step={f.step}
                value={values[f.key] ?? ""}
                onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                className="input mt-1"
              />
            </label>
          ))}
        </div>

        {err && <p className="text-danger text-sm mt-3">{err}</p>}

        <div className="flex gap-2 mt-4 mb-2">
          <Button onClick={save} disabled={busy} className="flex-1">
            {busy ? t("edit_sheet.saving") : t("edit_sheet.save")}
          </Button>
          {onDelete && (
            <Button variant="danger" onClick={del} disabled={busy}>
              {t("edit_sheet.delete")}
            </Button>
          )}
        </div>
    </SheetShell>
  );
}
